import { useState, useCallback, useEffect } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Briefcase, Plus, Send, Link2, Users, X, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function ReqEditor({ competences, value, onChange }) {
  const { t } = useLang();
  const add = () => onChange([...value, { competence_id: "", min_level: 3 }]);
  const set = (i, k, v) => onChange(value.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const rm = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {value.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={r.competence_id} onValueChange={(v) => set(i, "competence_id", v)}>
            <SelectTrigger className="flex-1" data-testid={`req-comp-${i}`}><SelectValue placeholder={t("req_comp_ph")} /></SelectTrigger>
            <SelectContent>{(competences || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(r.min_level)} onValueChange={(v) => set(i, "min_level", Number(v))}>
            <SelectTrigger className="w-24" data-testid={`req-lvl-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{t("lvl", { n })}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" variant="ghost" size="icon" onClick={() => rm(i)}><X className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="req-add"><Plus className="mr-1 h-4 w-4" />{t("add_req")}</Button>
    </div>
  );
}

function Applicants({ roleId, competences }) {
  const { t } = useLang();
  const [apps, setApps] = useState(null);
  const cName = (id) => (competences || []).find((c) => c.id === id)?.name || id;
  useEffect(() => { api.get(`/recruit/roles/${roleId}/applications`).then((r) => setApps(r.data)).catch((e) => toast.error(apiErr(e))); }, [roleId]);
  if (!apps) return <p className="mt-2 text-xs text-muted-foreground">{t("loading")}</p>;
  if (apps.length === 0) return <p className="mt-2 text-xs text-muted-foreground">{t("no_apps")}</p>;
  return (
    <div className="mt-2 space-y-2">
      {apps.map((a) => (
        <div key={a.id} className="rounded-md border p-2 text-sm" data-testid={`applicant-${a.id}`}>
          <div className="flex items-center gap-2"><span className="font-medium">{a.applicant_name}</span>
            <Badge variant={a.match_pct >= 80 ? "default" : a.match_pct >= 50 ? "secondary" : "outline"} className="ml-auto">{t("match_pct", { n: a.match_pct })}</Badge></div>
          {a.message && <p className="mt-1 text-muted-foreground">{a.message}</p>}
          {(a.gaps || []).length > 0 && <p className="mt-1 text-xs text-amber-700">{t("lacks", { list: a.gaps.map((g) => `${cName(g.competence_id)} (${t("lvl", { n: g.min_level })})`).join(", ") })}</p>}
        </div>
      ))}
    </div>
  );
}

function RoleCard({ role, competences, canManage, onChanged }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const cName = (id) => (competences || []).find((c) => c.id === id)?.name || id;
  const apply = async () => {
    try { const r = await api.post(`/recruit/roles/${role.id}/apply`, { message: msg }); toast.success(t("interest_toast", { n: r.data.match_pct })); onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const copyLink = () => {
    const url = `${window.location.origin}/internrekrytering?roll=${role.share_token}`;
    navigator.clipboard.writeText(url); toast.success(t("link_copied"));
  };
  const close = async () => { try { await api.patch(`/recruit/roles/${role.id}`, { status: role.status === "öppen" ? "stängd" : "öppen" }); onChanged(); } catch (e) { toast.error(apiErr(e)); } };
  const del = async () => { try { await api.delete(`/recruit/roles/${role.id}`); onChanged(); toast.success(t("removed")); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Card className="p-4" data-testid={`role-${role.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Briefcase className="h-4 w-4 text-accent" />
        <span className="font-medium">{role.title}</span>
        <Badge variant={role.status === "öppen" ? "default" : "secondary"}>{role.status === "öppen" ? t("role_open") : t("role_closed")}</Badge>
        {!canManage && <Badge variant={role.match_pct >= 80 ? "default" : role.match_pct >= 50 ? "secondary" : "outline"} className="ml-auto">{t("match_pct", { n: role.match_pct })}</Badge>}
        {canManage && <span className="ml-auto text-xs text-muted-foreground">{t("n_applied", { n: role.application_count })}</span>}
      </div>
      {role.description && <p className="mt-1 text-sm text-muted-foreground">{role.description}</p>}
      {(role.required_competences || []).length > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">{t("req_prefix", { list: role.required_competences.map((rc) => `${cName(rc.competence_id)} (${t("lvl", { n: rc.min_level })})`).join(", ") })}</p>
      )}
      {!canManage && (role.gaps || []).length > 0 && <p className="mt-1 text-xs text-amber-700">{t("you_lack", { list: role.gaps.map((g) => `${cName(g.competence_id)} (${t("lvl", { n: g.min_level })}, ${g.your_level})`).join(", ") })}</p>}

      {!canManage && role.status === "öppen" && (
        role.applied
          ? <Badge className="mt-2 gap-1"><CheckCircle2 className="h-3 w-3" />{t("interest_registered")}</Badge>
          : (
            <div className="mt-2 flex items-center gap-2">
              <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={t("motivation_ph")} data-testid={`apply-msg-${role.id}`} />
              <Button size="sm" onClick={apply} data-testid={`apply-${role.id}`}><Send className="mr-1 h-4 w-4" />{t("register_interest")}</Button>
            </div>
          )
      )}

      {canManage && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={copyLink} data-testid={`copy-link-${role.id}`}><Link2 className="mr-1 h-4 w-4" />{t("copy_link")}</Button>
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid={`show-apps-${role.id}`}><Users className="mr-1 h-4 w-4" />{open ? t("hide") : t("show")} {t("apps_word")} ({role.application_count})</Button>
          <Button size="sm" variant="ghost" onClick={close} data-testid={`toggle-${role.id}`}>{role.status === "öppen" ? t("close_btn") : t("open_btn")}</Button>
          <Button size="sm" variant="ghost" onClick={del} data-testid={`del-role-${role.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      )}
      {canManage && open && <Applicants roleId={role.id} competences={competences} />}
    </Card>
  );
}

export default function Internrekrytering() {
  const { t } = useLang();
  const { effectiveRole } = useAuth();
  const canManage = ["owner", "hr_admin", "chef"].includes(effectiveRole);
  const { data: roles, reload } = useApi("/recruit/roles");
  const { data: departments } = useApi("/departments");
  const { data: competences } = useApi("/competences");
  const [form, setForm] = useState({ title: "", description: "", department_id: "", business_area_id: "", required_competences: [] });

  const create = async () => {
    if (!form.title.trim()) return toast.error(t("enter_title"));
    try {
      await api.post("/recruit/roles", { ...form, required_competences: form.required_competences.filter((r) => r.competence_id) });
      toast.success(t("role_published"));
      setForm({ title: "", description: "", department_id: "", business_area_id: "", required_competences: [] });
      reload();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={t("rec_title")} icon={Briefcase}
        subtitle={canManage ? t("rec_sub_manage") : t("rec_sub_emp")} />

      {canManage && (
        <Card className="mb-6 grid gap-3 p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>{t("title_lbl")}</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="role-title" /></div>
            <div><Label>{t("dep_opt_lbl")}</Label>
              <Select value={form.department_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, department_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="role-dept"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{(departments || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>{t("desc_lbl")}</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="role-desc" /></div>
          <div><Label>{t("req_comps_lbl")}</Label><ReqEditor competences={competences} value={form.required_competences} onChange={(v) => setForm((f) => ({ ...f, required_competences: v }))} /></div>
          <div><Button onClick={create} data-testid="role-create"><Plus className="mr-1 h-4 w-4" />{t("publish_role")}</Button></div>
        </Card>
      )}

      {(roles || []).length === 0 ? <EmptyState icon={Briefcase} title={t("no_roles")} text={canManage ? t("no_roles_manage") : t("no_roles_emp")} /> : (
        <div className="space-y-3">
          {roles.map((r) => <RoleCard key={r.id} role={r} competences={competences} canManage={canManage} onChanged={reload} />)}
        </div>
      )}
    </div>
  );
}
